/**
 * Санки сохранения черновика квиза и пейволла вместе со снимком
 * DraftSaveSnapshot, вынесены из editorSlice.ts (WEB-2552, пачка 49, заход Р2
 * рефакторинга; «перенос токен в токен», тела не правились). Строковые имена
 * санков (`editor/saveQuizDraft`, `editor/savePaywallDraft`) прежние.
 * extraReducers этих санков остались в фасаде; фасад реэкспортирует имена.
 * Действие причины тарифного отказа берётся из `../actions` — не из фасада,
 * иначе круговой импорт (фасад слушает эти санки).
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { quizzesApi, paywallsApi } from "@/api";
import { extractApiError } from "@/api/errorStatus";
import type { Rule } from "@/logic-engine";
import {
  PAYMENT_REQUIRED_SENTINEL,
  TIER_LIMIT_SENTINEL,
} from "../saveRejection";
import type { SaveRejection } from "../saveRejection";
import type { EditorScreen } from "../types/screen";
import type { EditorSettings } from "../types/settings";
import type { EditorState } from "../state";
import { setTierLimitReason } from "../actions";
import type { SentDraftMeta } from "../sentDraft";
import { writeSessionOf } from "../openResource";
import type { WriteSessionMeta } from "../openResource";

/**
 * WEB-2668: сервер отбивает пустое имя и имя из одних пробелов (400
 * VALIDATION_NAME_EMPTY), а без поля имя не меняет. Сохранение черновика шлёт
 * имя вместе с содержимым, поэтому пустое имя в запрос не кладётся — иначе у
 * записи с пустым именем (старые данные, стёртое в шапке название) не
 * сохранялось бы ничего. Аргумент санка при этом не трогается: сверка
 * «правили ли после отправки» сравнивает имя хранилища с аргументом.
 */
function nameField(name: string): { name?: string } {
  return name.trim() === "" ? {} : { name };
}

/**
 * WEB-2551: снимок правил и отметки версии, который сохранение при уходе из
 * редактора передаёт аргументом. К этому моменту страница уже сбросила стор
 * (эффекты гаснут от родителя к ребёнку), и чтение стора давало `logic: []`
 * без `baseUpdatedAt`: сервер стирал правила и пропускал сверку версий.
 * Поле передано — стор за ним не читается; не передано — прежнее чтение
 * стора (обычный путь сохранения не меняется).
 */
export interface DraftSaveSnapshot {
  logic?: Rule[];
  baseUpdatedAt?: string;
}

/**
 * WEB-2552: успех санка несёт меткой `sentLogic` правила, которые ушли на
 * сервер (обычное сохранение читает их из хранилища, в аргументе их нет).
 * По метке обработчик успеха в фасаде решает, можно ли погасить «не
 * сохранено» (`../sentDraft.ts`). Отказ по-прежнему без типа значения.
 */
/**
 * WEB-2561: успех и отказ несут ещё и снимок сеанса записи `writeSession`
 * (`../openResource.ts`) — снятый на старте санка: держал ли редактор этот
 * ресурс и в каком сеансе открытия. По нему фасад не пускает ответ записи
 * прошлого сеанса (сохранение при уходе) в тот же квиз, открытый заново.
 */
type SaveThunkConfig = {
  fulfilledMeta: SentDraftMeta & WriteSessionMeta;
  rejectedMeta: WriteSessionMeta;
};

type SaveQuizDraftArg = {
  projectId: string;
  quizId: string;
  name: string;
  content: EditorScreen[];
  settings?: EditorSettings;
} & DraftSaveSnapshot;

type SavePaywallDraftArg = {
  projectId: string;
  paywallId: string;
  name: string;
  content: EditorScreen[];
  settings?: EditorSettings;
} & DraftSaveSnapshot;

export const saveQuizDraft = createAsyncThunk<Record<string, unknown>, SaveQuizDraftArg, SaveThunkConfig>(
  "editor/saveQuizDraft",
  async (
    {
      projectId,
      quizId,
      name,
      content,
      settings,
      logic: snapshotLogic,
      baseUpdatedAt: snapshotBase,
    },
    { rejectWithValue, fulfillWithValue, getState, dispatch },
  ) => {
    // WEB-2561: снимается первой строкой try — до любого await.
    let writeSession: WriteSessionMeta["writeSession"];
    try {
      const state = getState() as { editor: EditorState };
      writeSession = writeSessionOf(state.editor, "quiz", quizId);
      const logic = snapshotLogic ?? state.editor.logic;
      // WEB-1333 (п.5): черновик основан на этом снимке updatedAt — сервер
      // отклонит запись 409 STALE_DRAFT, если запись успела измениться.
      const baseUpdatedAt =
        snapshotBase ?? state.editor.serverUpdatedAt ?? undefined;
      const response = await quizzesApi.update(projectId, quizId, {
        ...nameField(name),
        content,
        settings,
        logic,
        ...(baseUpdatedAt !== undefined && { baseUpdatedAt }),
      });
      return fulfillWithValue(response.data as Record<string, unknown>, { sentLogic: logic, writeSession });
    } catch (error: unknown) {
      const err = (error ?? {}) as {
        message?: string;
        status?: number;
        code?: string;
        /** WEB-1908: причина денежного отказа рядом с общим кодом. */
        reasonCode?: string;
      };
      if (err.status === 402) {
        // WEB-1908: причина отказа (`PAYMENT_REQUIRED_*`) не помещается в
        // строку-примету — кладём её в состояние рядом, чтобы окно тарифа
        // редактора сказало человеку, что именно упёрлось. Пишется всегда,
        // в том числе `null`, — иначе висела бы причина прошлого отказа.
        dispatch(setTierLimitReason(err.reasonCode ?? null));
        // WEB-391: tier-limit (free-tier publish cap) is a recoverable upsell —
        // distinct from the WEB-136 wallet-balance fatal path.
        return rejectWithValue(
          // WEB-613: publish is now a FEATURE gate (code FEATURE_LOCKED); both it
          // and the TIER_LIMIT count-gate are recoverable tier upsells → PricingModal.
          err.code === "TIER_LIMIT" || err.code === "FEATURE_LOCKED"
            ? TIER_LIMIT_SENTINEL
            : PAYMENT_REQUIRED_SENTINEL,
          { writeSession },
        );
      }
      // WEB-657: classified reject — keep the HTTP status so handleAutoSave
      // can stop on 4xx and back off on 5xx/network instead of hot-looping.
      // WEB-1955: ту же тройку (статус/текст/код) собирает общий разборщик,
      // и вдобавок достаёт `reasonCode` денежного отказа.
      return rejectWithValue(extractApiError(err) satisfies SaveRejection, { writeSession });
    }
  },
);

export const savePaywallDraft = createAsyncThunk<Record<string, unknown>, SavePaywallDraftArg, SaveThunkConfig>(
  "editor/savePaywallDraft",
  async (
    {
      projectId,
      paywallId,
      name,
      content,
      settings,
      logic: snapshotLogic,
      baseUpdatedAt: snapshotBase,
    },
    { rejectWithValue, fulfillWithValue, getState, dispatch },
  ) => {
    // WEB-2561: снимается первой строкой try — до любого await.
    let writeSession: WriteSessionMeta["writeSession"];
    try {
      const state = getState() as { editor: EditorState };
      writeSession = writeSessionOf(state.editor, "paywall", paywallId);
      // WEB-2551: зеркало saveQuizDraft — снимок аргументом важнее стора.
      const logic = snapshotLogic ?? state.editor.logic;
      // WEB-1333 (п.5): зеркало saveQuizDraft — сверка baseUpdatedAt.
      const baseUpdatedAt =
        snapshotBase ?? state.editor.serverUpdatedAt ?? undefined;
      // WEB-347: appUrl/storeUrl removed from save payload — source = project.bridgeConfig.storeUrl.
      const response = await paywallsApi.update(projectId, paywallId, {
        ...nameField(name),
        content,
        settings,
        logic,
        ...(baseUpdatedAt !== undefined && { baseUpdatedAt }),
      });
      return fulfillWithValue(response.data as Record<string, unknown>, { sentLogic: logic, writeSession });
    } catch (error: unknown) {
      const err = (error ?? {}) as {
        message?: string;
        status?: number;
        code?: string;
        /** WEB-1908: причина денежного отказа рядом с общим кодом. */
        reasonCode?: string;
      };
      if (err.status === 402) {
        // WEB-1908: причина отказа (`PAYMENT_REQUIRED_*`) не помещается в
        // строку-примету — кладём её в состояние рядом, чтобы окно тарифа
        // редактора сказало человеку, что именно упёрлось. Пишется всегда,
        // в том числе `null`, — иначе висела бы причина прошлого отказа.
        dispatch(setTierLimitReason(err.reasonCode ?? null));
        // WEB-391: tier-limit (free-tier publish cap) is a recoverable upsell —
        // distinct from the WEB-136 wallet-balance fatal path.
        return rejectWithValue(
          // WEB-613: publish is now a FEATURE gate (code FEATURE_LOCKED); both it
          // and the TIER_LIMIT count-gate are recoverable tier upsells → PricingModal.
          err.code === "TIER_LIMIT" || err.code === "FEATURE_LOCKED"
            ? TIER_LIMIT_SENTINEL
            : PAYMENT_REQUIRED_SENTINEL,
          { writeSession },
        );
      }
      // WEB-657: classified reject — see saveQuizDraft.
      return rejectWithValue(extractApiError(err) satisfies SaveRejection, { writeSession });
    }
  },
);
